/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TrackingIDs } from '../@types/Tracking';
import { injectsGaGtmPixel } from '../helpers/utils/trackingLoader';

declare global {
  interface Window {
    dataLayer: any[];
    fbq?: (...args: any[]) => void;
    _fbq?: any;
    gtag?: any;
  }
}

interface TrackingContextType {
  setTrackingIDs: (ids: TrackingIDs) => void;
  trackEvent: (event: string, data?: Record<string, any>) => void;
  trackingIDs: TrackingIDs;
}

const TrackingContext = createContext<TrackingContextType | undefined>(
  undefined,
);

export const TrackingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [trackingIDs, setTracking] = useState<TrackingIDs>({});
  const location = useLocation();

  useEffect(() => {
    if (!trackingIDs.idPixel && !trackingIDs.idGA && !trackingIDs.idGTM) return;

    if (!trackingIDs.tipo) return;

    injectsGaGtmPixel(trackingIDs);
  }, [trackingIDs]);

  useEffect(() => {
    const pagePath = location.pathname + location.search;

    if (trackingIDs.idGA && window.gtag) {
      window.gtag('config', trackingIDs.idGA, {
        page_path: pagePath,
      });
      console.log('[GA] ViewContent enviado:', pagePath);
    }

    if (trackingIDs.idGTM && window.dataLayer) {
      window.dataLayer.push({
        event: 'ViewContent',
        page: pagePath,
      });
      console.log('[GTM] ViewContent enviado:', pagePath);
    }

    if (trackingIDs.idPixel && window.fbq) {
      window.fbq('track', 'ViewContent', {
        path: pagePath,
      });
      console.log('[Pixel] ViewContent enviado:', pagePath);
    }
  }, [location, trackingIDs.idGA, trackingIDs.idGTM, trackingIDs.idPixel]);

  const trackEvent = (event: string, data?: Record<string, any>): void => {
    // Google Analytics (GA4)
    if (trackingIDs.idGA && typeof window.gtag === 'function') {
      window.gtag('event', event, data);
      console.log('[GA] Evento enviado:', event, data);
    }

    // Google Tag Manager (via dataLayer)
    if (trackingIDs.idGTM && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event, ...data });
      console.log('[GTM] Evento enviado:', event, data);
    }

    // Meta Pixel
    if (trackingIDs.idPixel && typeof window.fbq === 'function') {
      window.fbq('trackCustom', event, data);
      console.log('[MetaPixel] Evento enviado:', event, data);
    }
  };

  return (
    <TrackingContext.Provider
      value={{
        trackingIDs,
        setTrackingIDs: setTracking,
        trackEvent,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingContext = (): TrackingContextType => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error(
      'useTrackingContext deve ser usado dentro do TrackingProvider',
    );
  }
  return context;
};
